import { useEffect } from 'react';
import urlTemplate from 'url-template';
import type { Uuid } from 'src/apis/api-utilities';
import type { SurrenderRequestDetailsResponse } from 'src/apis/surrender-service/types';
import type { SurrenderRootRequest } from 'src/apis/surrender-service/use-surrender-root';
import { useAxios } from 'src/hooks/use-axios';
import { Forbidden } from 'src/utils/errors';

type HookConfig = {
  surrenderRoot: SurrenderRootRequest;
  surrenderRequestId: Uuid | undefined;
};

type ExecuteProps = {
  url: string;
};

export function useSurrenderRequestDetails({
  surrenderRoot,
  surrenderRequestId,
}: HookConfig) {
  const request = useAxios(
    (axios, baseConfig, { url }: ExecuteProps) =>
      axios.request<SurrenderRequestDetailsResponse>({
        ...baseConfig,
        url,
      }),
    { neededOnPageLoad: true }
  );

  useEffect(() => {
    if (!surrenderRoot.response || !surrenderRequestId) return;
    if (!surrenderRoot.response.data._links.getSurrenderRequestDetails)
      throw new Forbidden();

    const templatedUrl =
      surrenderRoot.response.data._links.getSurrenderRequestDetails.href;
    const url = urlTemplate.parse(templatedUrl).expand({ surrenderRequestId });

    request.execute({ url });
    // oxlint-disable-next-line exhaustive-deps
  }, [surrenderRoot.response, surrenderRequestId]);

  if (!surrenderRoot.response || !surrenderRequestId) return;
  if (!surrenderRoot.response.data._links.getSurrenderRequestDetails)
    throw new Forbidden();

  return request;
}

export type SurrenderRequestDetailsRequest = ReturnType<
  typeof useSurrenderRequestDetails
>;
